import { ApolloError, gql } from "@apollo/client";
import { useState } from "react";

import { useB2CQuery } from "../client";
import {
  GET_IMAGING_AND_LAB_RESULTS,
  GET_IMAGING_AND_LAB_RESULTS_patient_imagingResults_items,
  GET_IMAGING_AND_LAB_RESULTS_patient_labResults_items,
} from "../schema/types";

export type TestResult = {
  id: number;
  description: string;
  date: string;
  type: TestResultType;
};

export enum TestResultType {
  LAB = "lab",
  IMAGING = "imaging",
}

export const labResultsQuery = gql`
  query GET_LABS {
    patient {
      labResults {
        items {
          description
          labResultId
          portalPublishedDatetime
        }
      }
    }
  }
`;

export const imagingResultsQuery = gql`
  query GET_IMAGING_RESULTS {
    patient {
      imagingResults {
        items {
          imagingResultId
          documentDescription
          createdDatetime
        }
      }
    }
  }
`;

export const imagingAndLabResultsQuery = gql`
  query GET_IMAGING_AND_LAB_RESULTS {
    patient {
      imagingResults {
        items {
          imagingResultId
          documentDescription
          createdDatetime
        }
      }
      labResults {
        items {
          description
          labResultId
          portalPublishedDatetime
        }
      }
    }
  }
`;

const useTestResults = (): {
  error: ApolloError | undefined;
  loading: boolean;
  data: TestResult[] | null | undefined;
} => {
  const [testData, setTestData] = useState<TestResult[]>();

  const {
    data: imagingAndLabResultsData,
    error: imagingAndLabResultsError,
    loading: imagingAndLabResultsLoading,
  } = useB2CQuery<GET_IMAGING_AND_LAB_RESULTS>(imagingAndLabResultsQuery, {
    onCompleted: () => {
      const labResults = imagingAndLabResultsData?.patient.labResults?.items ?? [];
      const imagingResults = imagingAndLabResultsData?.patient.imagingResults?.items ?? [];

      const mergedData = mergeAndSortResults(labResults, imagingResults);

      setTestData(mergedData);
    },
  });

  const error = imagingAndLabResultsError;
  const loading = imagingAndLabResultsLoading;

  return {
    error,
    loading,
    data: testData,
  };
};

const mergeAndSortResults = (
  labResultsItems: GET_IMAGING_AND_LAB_RESULTS_patient_labResults_items[],
  imagingResultsItems: GET_IMAGING_AND_LAB_RESULTS_patient_imagingResults_items[],
): TestResult[] => {
  const labResults: TestResult[] = labResultsItems.map((item) => {
    return {
      id: item.labResultId,
      description: item.description || "",
      date: item.portalPublishedDatetime,
      type: TestResultType.LAB,
    };
  });

  const imagingResults: TestResult[] = imagingResultsItems
    .map((item) => {
      if (item.imagingResultId !== null && item.documentDescription !== null) {
        return {
          id: item.imagingResultId,
          description: item.documentDescription,
          date: item.createdDatetime,
          type: TestResultType.IMAGING,
        };
      } else {
        return undefined;
      }
    })
    .filter((item): item is TestResult => item !== undefined);

  return [...labResults, ...imagingResults]
    .filter((item) => item.date !== null)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
};

export default useTestResults;
